import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Archive, RotateCcw, Trash2, AlertTriangle, Search, Filter, Inbox, Building2, User, Briefcase, FileText, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ENTITY_META = {
  brand:      { label: 'Brands',       icon: Building2, color: 'text-[#FF4343]' },
  executive:  { label: 'Executives',   icon: User,      color: 'text-blue-400' },
  campaign:   { label: 'Campaigns',    icon: Briefcase, color: 'text-purple-400' },
  submission: { label: 'Submissions',  icon: FileText,  color: 'text-yellow-400' },
  lead:       { label: 'Intake Leads', icon: Mail,      color: 'text-green-400' },
};

// ============================================================
// Reusable confirmation modal
// ============================================================
function ConfirmModal({ open, onClose, onConfirm, title, body, confirmLabel = 'Confirm', danger = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="cleanup-confirm-modal">
      <div className="bg-[#111827] border border-white/10 max-w-md w-full p-5">
        <div className="flex items-start gap-3 mb-4">
          {danger && <AlertTriangle className="w-5 h-5 text-[#FF4343] flex-shrink-0 mt-0.5" />}
          <div>
            <h3 className="text-sm font-semibold text-white mb-1">{title}</h3>
            <div className="text-xs text-[#9CA3AF] leading-relaxed">{body}</div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} data-testid="cleanup-cancel-btn"
            className="text-xs px-4 py-2 text-[#9CA3AF] hover:text-white">Cancel</button>
          <button onClick={onConfirm} data-testid="cleanup-confirm-btn"
            className={`text-xs px-4 py-2 font-semibold ${danger ? 'bg-[#FF4343] text-[#090E33] hover:bg-[#E03030]' : 'bg-white text-[#090E33] hover:bg-[#E5E7EB]'}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Row action buttons (Archive · Restore · Delete)
// ============================================================
function RowActions({ entity, ident, archived, onChange, label }) {
  const [confirm, setConfirm] = useState(null); // { mode, force }

  const doArchive = async () => {
    try {
      await axios.post(`${API}/admin/cleanup/${entity}/${ident}/archive`, {}, { headers: getAuthHeaders() });
      toast.success(`${ENTITY_META[entity].label.slice(0, -1)} archived`);
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to archive'); }
    finally { setConfirm(null); }
  };
  const doRestore = async () => {
    try {
      await axios.post(`${API}/admin/cleanup/${entity}/${ident}/restore`, {}, { headers: getAuthHeaders() });
      toast.success('Restored');
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to restore'); }
    finally { setConfirm(null); }
  };
  const doDelete = async (force) => {
    try {
      await axios.delete(`${API}/admin/cleanup/${entity}/${ident}`, { headers: getAuthHeaders(), params: force ? { force: 'true' } : {} });
      toast.success('Permanently deleted');
      onChange && onChange();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && typeof d === 'object') {
        const links = Object.entries(d.links || {}).map(([k, v]) => `${v} ${k}`).join(', ');
        setConfirm({ mode: 'force', linksLabel: links });
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to delete');
        setConfirm(null);
      }
    }
  };

  return (
    <>
      <div className="flex gap-1 justify-end">
        {!archived && (
          <button onClick={() => setConfirm({ mode: 'archive' })} data-testid={`archive-${entity}-${ident}`}
            className="text-[10px] uppercase font-mono px-2 py-1 border border-yellow-400/40 text-yellow-400 hover:bg-yellow-400 hover:text-[#090E33]">
            <Archive className="w-3 h-3 inline mr-0.5" /> Archive
          </button>
        )}
        {archived && (
          <button onClick={() => doRestore()} data-testid={`restore-${entity}-${ident}`}
            className="text-[10px] uppercase font-mono px-2 py-1 border border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981] hover:text-[#090E33]">
            <RotateCcw className="w-3 h-3 inline mr-0.5" /> Restore
          </button>
        )}
        <button onClick={() => setConfirm({ mode: 'delete' })} data-testid={`delete-${entity}-${ident}`}
          className="text-[10px] uppercase font-mono px-2 py-1 border border-red-400/50 text-red-400 hover:bg-red-400 hover:text-white">
          <Trash2 className="w-3 h-3 inline mr-0.5" /> Delete
        </button>
      </div>

      <ConfirmModal
        open={confirm?.mode === 'archive'}
        onClose={() => setConfirm(null)}
        onConfirm={doArchive}
        title={`Archive this ${ENTITY_META[entity].label.slice(0, -1).toLowerCase()}?`}
        body={<>It will be hidden from default lists and any linked records will also be archived. You can restore it any time from the <strong>Archived</strong> tab. <br/><br/><span className="text-white">{label}</span></>}
        confirmLabel="Archive"
      />
      <ConfirmModal
        open={confirm?.mode === 'delete'}
        onClose={() => setConfirm(null)}
        onConfirm={() => doDelete(false)}
        title={`Permanently delete this ${ENTITY_META[entity].label.slice(0, -1).toLowerCase()}?`}
        body={<>This <strong>cannot be undone</strong>. If linked records exist, you'll be asked to cascade-delete.<br/><br/><span className="text-white">{label}</span></>}
        confirmLabel="Delete forever"
        danger
      />
      <ConfirmModal
        open={confirm?.mode === 'force'}
        onClose={() => setConfirm(null)}
        onConfirm={() => doDelete(true)}
        title="Cascade-delete linked records?"
        body={<>This row has linked records ({confirm?.linksLabel}). Deleting will <strong>permanently remove</strong> all of them too. This cannot be undone.</>}
        confirmLabel="Cascade delete"
        danger
      />
    </>
  );
}

// ============================================================
// Entity-specific tables
// ============================================================
function BrandsTable({ onChange, archived = false, search }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      if (archived) {
        const { data } = await axios.get(`${API}/admin/cleanup/archived`, { headers: getAuthHeaders() });
        setRows(data.brand || []);
      } else {
        const { data } = await axios.get(`${API}/admin/signups`, { headers: getAuthHeaders(), params: { role: 'brand', ...(search ? { search } : {}) } });
        setRows(data.brands || []);
      }
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [archived, search]);

  if (loading) return <p className="text-[#9CA3AF] text-xs p-4">Loading…</p>;
  if (rows.length === 0) return <p className="text-[#9CA3AF] text-xs p-4">No brands{archived ? ' archived' : ''}.</p>;

  return (
    <div className="overflow-x-auto bg-[#111827] border border-white/10">
      <table className="w-full text-xs">
        <thead className="bg-white/5 text-[10px] uppercase font-mono text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 text-left">Brand</th>
            <th className="px-3 py-2 text-left">Email</th>
            <th className="px-3 py-2 text-left">Industry</th>
            {!archived && <th className="px-3 py-2 text-left">Campaigns</th>}
            <th className="px-3 py-2 text-left">{archived ? 'Archived' : 'Signed Up'}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(b => (
            <tr key={b.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`cleanup-brand-${b.id}`}>
              <td className="px-3 py-2 text-white font-semibold">{b.name}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{b.portal_email || b.email}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{b.industry || '—'}</td>
              {!archived && <td className="px-3 py-2 text-white font-mono">{b.campaign_count ?? 0}</td>}
              <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(b.archived_at || b.created_at || '').slice(0, 10)}</td>
              <td className="px-3 py-2">
                <RowActions entity="brand" ident={b.id} archived={archived} onChange={() => { fetchRows(); onChange && onChange(); }} label={`${b.name} (${b.portal_email || b.email})`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function XEnrichCell({ exec, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [inputOpen, setInputOpen] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const profile = exec.x_profile;
  const handle = (exec.x_handle || profile?.handle || '').replace(/^@/, '');

  const enrich = async (handleToUse) => {
    const h = (handleToUse || '').replace(/^@/, '').trim();
    if (!h) {
      toast.error('Please enter an X handle');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/x/enrich-exec`,
        { user_id: exec.id, handle: h, force: true },
        { headers: getAuthHeaders() });
      toast.success(`@${h} ${profile ? 'refreshed' : 'linked'}${data.from_cache ? ' (cached)' : ''}`);
      setInputOpen(false);
      setInputVal('');
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    } finally { setBusy(false); }
  };

  // No handle yet → admin-add inline input
  if (!handle) {
    if (inputOpen) {
      return (
        <div className="flex items-center gap-1.5" data-testid={`x-add-${exec.id}`}>
          <span className="text-[#9CA3AF] text-xs">@</span>
          <input
            value={inputVal}
            onChange={e => setInputVal(e.target.value.replace('@', ''))}
            placeholder="handle"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') enrich(inputVal); if (e.key === 'Escape') { setInputOpen(false); setInputVal(''); } }}
            data-testid={`x-add-input-${exec.id}`}
            className="w-24 px-2 py-0.5 bg-[#090E33] border border-white/15 text-white text-xs focus:outline-none focus:border-[#FF4343]"
          />
          <button onClick={() => enrich(inputVal)} disabled={busy || !inputVal}
            data-testid={`x-add-save-${exec.id}`}
            className="text-[10px] font-mono uppercase px-2 py-0.5 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] disabled:opacity-50">
            {busy ? '...' : 'Save'}
          </button>
          <button onClick={() => { setInputOpen(false); setInputVal(''); }}
            className="text-[10px] text-[#6B7280] hover:text-white">✕</button>
        </div>
      );
    }
    return (
      <button onClick={() => setInputOpen(true)} data-testid={`x-add-btn-${exec.id}`}
        className="text-[10px] font-mono uppercase px-2 py-0.5 border border-white/10 text-[#6B7280] hover:border-[#FF4343] hover:text-[#FF4343]">
        + Add @
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid={`x-cell-${exec.id}`}>
      <a href={`https://x.com/${handle}`} target="_blank" rel="noopener noreferrer"
        className="text-[#FF4343] hover:underline">@{handle}</a>
      {profile && (
        <span className="text-[10px] font-mono text-[#9CA3AF]">
          {(profile.followers_count ?? 0).toLocaleString()} followers
          {profile.verified && <span className="ml-1 text-blue-400">✓</span>}
        </span>
      )}
      <button onClick={() => enrich(handle)} disabled={busy} data-testid={`enrich-x-${exec.id}`}
        title={profile ? 'Refresh from X' : 'Enrich from X'}
        className="text-[10px] font-mono uppercase px-2 py-0.5 border border-white/15 text-[#9CA3AF] hover:border-[#FF4343] hover:text-[#FF4343] disabled:opacity-50">
        {busy ? '...' : (profile ? 'Refresh' : 'Enrich')}
      </button>
    </div>
  );
}

function ExecutivesTable({ onChange, archived = false, search }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      if (archived) {
        const { data } = await axios.get(`${API}/admin/cleanup/archived`, { headers: getAuthHeaders() });
        setRows(data.executive || []);
      } else {
        const { data } = await axios.get(`${API}/admin/signups`, { headers: getAuthHeaders(), params: { role: 'executive', ...(search ? { search } : {}) } });
        setRows(data.executives || []);
      }
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [archived, search]);

  if (loading) return <p className="text-[#9CA3AF] text-xs p-4">Loading…</p>;
  if (rows.length === 0) return <p className="text-[#9CA3AF] text-xs p-4">No executives{archived ? ' archived' : ''}.</p>;

  return (
    <div className="overflow-x-auto bg-[#111827] border border-white/10">
      <table className="w-full text-xs">
        <thead className="bg-white/5 text-[10px] uppercase font-mono text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Email</th>
            <th className="px-3 py-2 text-left">Company</th>
            {!archived && <th className="px-3 py-2 text-left">X Profile</th>}
            <th className="px-3 py-2 text-left">{archived ? 'Archived' : 'Signed Up'}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(e => (
            <tr key={e.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`cleanup-exec-${e.id}`}>
              <td className="px-3 py-2 text-white font-semibold">{e.name}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{e.email}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{e.current_company || '—'}</td>
              {!archived && <td className="px-3 py-2"><XEnrichCell exec={e} onUpdated={fetchRows} /></td>}
              <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(e.archived_at || e.created_at || '').slice(0, 10)}</td>
              <td className="px-3 py-2">
                <RowActions entity="executive" ident={e.id} archived={archived} onChange={() => { fetchRows(); onChange && onChange(); }} label={`${e.name} (${e.email})`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CampaignsTable({ onChange, archived = false, search }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      if (archived) {
        const { data } = await axios.get(`${API}/admin/cleanup/archived`, { headers: getAuthHeaders() });
        setRows(data.campaign || []);
      } else {
        const { data } = await axios.get(`${API}/admin/cleanup/campaigns`, { headers: getAuthHeaders() });
        let list = data.campaigns || [];
        if (search) {
          const s = search.toLowerCase();
          list = list.filter(c => (c.title || '').toLowerCase().includes(s) || (c.brand_name || '').toLowerCase().includes(s));
        }
        setRows(list);
      }
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [archived, search]);

  if (loading) return <p className="text-[#9CA3AF] text-xs p-4">Loading…</p>;
  if (rows.length === 0) return <p className="text-[#9CA3AF] text-xs p-4">No campaigns{archived ? ' archived' : ''}.</p>;

  return (
    <div className="overflow-x-auto bg-[#111827] border border-white/10">
      <table className="w-full text-xs">
        <thead className="bg-white/5 text-[10px] uppercase font-mono text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 text-left">Title</th>
            <th className="px-3 py-2 text-left">Brand</th>
            <th className="px-3 py-2 text-left">Offering</th>
            {!archived && <th className="px-3 py-2 text-left">Submissions</th>}
            <th className="px-3 py-2 text-left">{archived ? 'Archived' : 'Created'}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`cleanup-campaign-${c.id}`}>
              <td className="px-3 py-2 text-white font-semibold">{c.title}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{c.brand_name || c.brand_id || '—'}</td>
              <td className="px-3 py-2 text-[#9CA3AF] font-mono text-[10px]">{c.offering_type || '—'}</td>
              {!archived && <td className="px-3 py-2 text-white font-mono">{c.submissions_count ?? 0}</td>}
              <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(c.archived_at || c.created_at || '').slice(0, 10)}</td>
              <td className="px-3 py-2">
                <RowActions entity="campaign" ident={c.id} archived={archived} onChange={() => { fetchRows(); onChange && onChange(); }} label={c.title} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubmissionsTable({ onChange, archived = false, search }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      if (archived) {
        const { data } = await axios.get(`${API}/admin/cleanup/archived`, { headers: getAuthHeaders() });
        setRows(data.submission || []);
      } else {
        const { data } = await axios.get(`${API}/admin/cleanup/submissions`, { headers: getAuthHeaders() });
        let list = data.submissions || [];
        if (search) {
          const s = search.toLowerCase();
          list = list.filter(p => (p.campaign_title || '').toLowerCase().includes(s) || (p.user_email || '').toLowerCase().includes(s) || (p.user_name || '').toLowerCase().includes(s));
        }
        setRows(list);
      }
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [archived, search]);

  if (loading) return <p className="text-[#9CA3AF] text-xs p-4">Loading…</p>;
  if (rows.length === 0) return <p className="text-[#9CA3AF] text-xs p-4">No submissions{archived ? ' archived' : ''}.</p>;

  return (
    <div className="overflow-x-auto bg-[#111827] border border-white/10">
      <table className="w-full text-xs">
        <thead className="bg-white/5 text-[10px] uppercase font-mono text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 text-left">Campaign</th>
            <th className="px-3 py-2 text-left">Executive</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">{archived ? 'Archived' : 'Created'}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`cleanup-submission-${p.id}`}>
              <td className="px-3 py-2 text-white">{p.campaign_title || p.campaign_id}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{p.user_name || p.user_email || p.user_id}</td>
              <td className="px-3 py-2 text-[#9CA3AF] font-mono text-[10px] uppercase">{p.status || '—'}</td>
              <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(p.archived_at || p.created_at || '').slice(0, 10)}</td>
              <td className="px-3 py-2">
                <RowActions entity="submission" ident={p.id} archived={archived} onChange={() => { fetchRows(); onChange && onChange(); }} label={`${p.campaign_title} → ${p.user_email}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadsTable({ onChange, archived = false, search }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      if (archived) {
        const { data } = await axios.get(`${API}/admin/cleanup/archived`, { headers: getAuthHeaders() });
        setRows(data.lead || []);
      } else {
        const { data } = await axios.get(`${API}/admin/cleanup/leads`, { headers: getAuthHeaders() });
        let list = data.leads || [];
        if (search) {
          const s = search.toLowerCase();
          list = list.filter(l => (l.brand_name || '').toLowerCase().includes(s) || (l.email || '').toLowerCase().includes(s) || (l.contact_name || '').toLowerCase().includes(s));
        }
        setRows(list);
      }
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [archived, search]);

  if (loading) return <p className="text-[#9CA3AF] text-xs p-4">Loading…</p>;
  if (rows.length === 0) return <p className="text-[#9CA3AF] text-xs p-4">No leads{archived ? ' archived' : ''}.</p>;

  return (
    <div className="overflow-x-auto bg-[#111827] border border-white/10">
      <table className="w-full text-xs">
        <thead className="bg-white/5 text-[10px] uppercase font-mono text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 text-left">Brand</th>
            <th className="px-3 py-2 text-left">Contact</th>
            <th className="px-3 py-2 text-left">Email</th>
            <th className="px-3 py-2 text-left">Industry</th>
            <th className="px-3 py-2 text-left">{archived ? 'Archived' : 'Submitted'}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(l => (
            <tr key={l.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`cleanup-lead-${l.id}`}>
              <td className="px-3 py-2 text-white font-semibold">{l.brand_name || '—'}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{l.contact_name || '—'}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{l.email}</td>
              <td className="px-3 py-2 text-[#9CA3AF]">{l.industry || '—'}</td>
              <td className="px-3 py-2 text-[10px] text-[#6B7280]">{(l.archived_at || l.created_at || '').slice(0, 10)}</td>
              <td className="px-3 py-2">
                <RowActions entity="lead" ident={l.id} archived={archived} onChange={() => { fetchRows(); onChange && onChange(); }} label={`${l.brand_name} (${l.email})`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Bulk Cleanup section
// ============================================================
function BulkCleanupCard() {
  const [entity, setEntity] = useState('brand');
  const [emailPattern, setEmailPattern] = useState('');
  const [olderThan, setOlderThan] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/cleanup/bulk-delete`, {
        entity,
        email_pattern: emailPattern || null,
        older_than_days: olderThan ? parseInt(olderThan, 10) : null,
        dry_run: dryRun,
      }, { headers: getAuthHeaders() });
      setResult(data);
      if (!dryRun && data.deleted > 0) toast.success(`Deleted ${data.deleted} record(s)`);
      if (!dryRun && data.deleted === 0) toast.message('Nothing deleted — see results below');
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk cleanup failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[#111827] border border-white/10 p-5" data-testid="bulk-cleanup-card">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold text-white">Bulk cleanup — only deletes unlinked records</h3>
          <p className="text-xs text-[#9CA3AF] mt-0.5 leading-relaxed">Records with any linked children (e.g. a brand with campaigns) are <strong>blocked</strong> and shown for manual review. Always click <strong>Preview</strong> first.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase font-mono text-[#6B7280] mb-1 block">Entity</label>
          <select value={entity} onChange={e => setEntity(e.target.value)} data-testid="bulk-entity"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
            <option value="brand">Brands</option>
            <option value="executive">Executives</option>
            <option value="lead">Intake Leads</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase font-mono text-[#6B7280] mb-1 block">Email contains (optional)</label>
          <input value={emailPattern} onChange={e => setEmailPattern(e.target.value)} placeholder="e.g. @freddyleads-qa.com" data-testid="bulk-email-pattern"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-mono text-[#6B7280] mb-1 block">Older than (days)</label>
          <input type="number" value={olderThan} onChange={e => setOlderThan(e.target.value)} placeholder="e.g. 90" data-testid="bulk-older-than"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => run(true)} disabled={busy} data-testid="bulk-preview-btn"
          className="border border-white/20 text-white hover:bg-white/5 px-4 py-2 text-xs font-semibold disabled:opacity-50">
          {busy ? 'Working…' : 'Preview (dry run)'}
        </button>
        <button onClick={() => run(false)} disabled={busy || !result || result.safe_to_delete === 0} data-testid="bulk-delete-btn"
          className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-4 py-2 text-xs font-semibold disabled:opacity-50">
          Delete {result?.safe_to_delete || 0} record(s)
        </button>
      </div>

      {result && (
        <div className="mt-4 bg-[#090E33] border border-white/10 p-3 text-xs space-y-2" data-testid="bulk-result">
          <div className="flex gap-4 flex-wrap">
            <span className="text-[#9CA3AF]">Matched: <strong className="text-white">{result.candidates}</strong></span>
            <span className="text-[#10B981]">Safe to delete: <strong>{result.safe_to_delete}</strong></span>
            <span className="text-red-400">Blocked (linked): <strong>{result.blocked?.length || 0}</strong></span>
            {!result.dry_run && <span className="text-[#FF4343]">Deleted: <strong>{result.deleted}</strong></span>}
          </div>
          {result.blocked?.length > 0 && (
            <div className="border-t border-white/10 pt-2">
              <p className="text-[10px] uppercase font-mono text-[#6B7280] mb-1">Blocked — handle these individually:</p>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {result.blocked.map((b, i) => (
                  <li key={i} className="text-[11px] text-[#9CA3AF]">
                    {b.label} — {Object.entries(b.links || {}).map(([k, v]) => `${v} ${k}`).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main Cleanup Tab
// ============================================================
export default function CleanupTab() {
  const [mode, setMode] = useState('active'); // 'active' | 'archived' | 'bulk'
  const [entity, setEntity] = useState('brand');
  const [search, setSearch] = useState('');
  const [totals, setTotals] = useState(null);

  const refreshTotals = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cleanup/archived`, { headers: getAuthHeaders() });
      setTotals(data.totals || {});
    } catch {}
  };
  useEffect(() => { refreshTotals(); }, []);

  const Table = useMemo(() => ({
    brand: BrandsTable, executive: ExecutivesTable, campaign: CampaignsTable, submission: SubmissionsTable, lead: LeadsTable,
  })[entity], [entity]);

  return (
    <div className="space-y-5" data-testid="admin-cleanup-tab">
      <div>
        <h2 className="font-display text-xl font-bold">Cleanup <span className="text-[#FF4343]">& Archive</span></h2>
        <p className="text-[10px] text-[#6B7280] mt-1">Archive hides rows from default lists (recoverable). Delete is permanent.</p>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {[
          { k: 'active',   l: 'Active records' },
          { k: 'archived', l: `Archived${totals ? ` (${Object.values(totals).reduce((a, b) => a + b, 0)})` : ''}` },
          { k: 'bulk',     l: 'Bulk cleanup' },
        ].map(t => (
          <button key={t.k} onClick={() => setMode(t.k)} data-testid={`cleanup-mode-${t.k}`}
            className={`px-4 py-2 text-xs uppercase font-mono tracking-wider border-b-2 ${mode === t.k ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent hover:text-white'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {mode !== 'bulk' && (
        <>
          {/* Entity selector */}
          <div className="flex flex-wrap gap-1">
            {Object.entries(ENTITY_META).map(([k, m]) => {
              const Icon = m.icon;
              const count = mode === 'archived' ? totals?.[k] : undefined;
              return (
                <button key={k} onClick={() => setEntity(k)} data-testid={`cleanup-entity-${k}`}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2 border ${entity === k ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF] hover:text-white'}`}>
                  <Icon className="w-3.5 h-3.5" /> {m.label}
                  {count !== undefined && <span className="text-[10px] font-mono opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>

          {/* Search */}
          {entity !== 'submission' && (
            <div className="flex gap-2">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" data-testid="cleanup-search"
                className="flex-1 px-3 py-2 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
            </div>
          )}

          {/* Table */}
          {Table && <Table archived={mode === 'archived'} search={search} onChange={refreshTotals} />}
        </>
      )}

      {mode === 'bulk' && <BulkCleanupCard />}
    </div>
  );
}
